import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Slider } from "@/components/ui/slider";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { ProfileFormData } from "@/pages/ProfileCreation";
import { Loader2 } from "lucide-react";
import { useState, useEffect, useRef } from "react";
import { CompactImportanceSelector } from "./CompactImportanceSelector";
import { ImportanceLevel } from "@/utils/weightedCompatibility";
import {
  ETHNICITY_SEEKING,
  SECT_SEEKING,
  MASJID_ATTENDANCE_SEEKING,
  HIJAB_PREFERENCE,
  BEARD_PREFERENCE,
  CITIZENSHIP_STATUS_SEEKING,
  MARITAL_STATUS_SEEKING,
  EDUCATION_LEVEL_SEEKING,
  RELIGIOSITY_LEVEL_SEEKING,
  SEEKING_LOCATION_PREFERENCE,
} from "@/lib/enums";

const createSchema = (isMale: boolean) => z.object({
  seeking_age_min: z.preprocess((val) => {
    if (typeof val === "number" && !Number.isNaN(val)) return val;
    if (typeof val === "string" && val.trim() !== "") return Number(val);
    return 22; // safe default
  }, z.number().min(18, "Minimum age must be at least 18").max(60, "Minimum age must be 60 or less")),
  seeking_age_max: z.preprocess((val) => {
    if (typeof val === "number" && !Number.isNaN(val)) return val;
    if (typeof val === "string" && val.trim() !== "") return Number(val);
    return 35; // safe default
  }, z.number().min(18, "Maximum age must be at least 18").max(60, "Maximum age must be 60 or less")),
  seeking_height_min_feet: z.union([z.number().min(4, "Height must be between 4 and 7 feet").max(7, "Height must be between 4 and 7 feet"), z.null(), z.undefined()]).optional(),
  seeking_height_min_inches: z.union([z.number().min(0, "Inches must be between 0 and 11").max(11, "Inches must be between 0 and 11"), z.null(), z.undefined()]).optional(),
  seeking_height_max_feet: z.union([z.number().min(4, "Height must be between 4 and 7 feet").max(7, "Height must be between 4 and 7 feet"), z.null(), z.undefined()]).optional(),
  seeking_height_max_inches: z.union([z.number().min(0, "Inches must be between 0 and 11").max(11, "Inches must be between 0 and 11"), z.null(), z.undefined()]).optional(),
  seeking_ethnicity: z.array(z.string()).optional(),
  seeking_sect: z.array(z.string()).optional(),
  seeking_religiosity: z.array(z.string()).optional(),
  seeking_masjid_attendance_min: z.string().nullable().optional(),
  seeking_location_preference: z.string().trim().max(100, "Location preference must be 100 characters or less").nullable().optional(),
  seeking_hijab_preference: z.string().nullable().optional(),
  seeking_beard_preference: z.string().nullable().optional(),
  seeking_citizenship: z.array(z.string()).optional(),
  seeking_marital_status: z.array(z.string()).optional(),
  seeking_children_ok: z.boolean(),
  seeking_convert_ok: z.boolean(),
  seeking_divorced_ok: z.boolean(),
  seeking_personality_min: z.union([z.number().min(1, "Personality must be at least 1").max(10, "Personality must be 10 or less"), z.null(), z.undefined()]).optional(),
  seeking_personality_max: z.union([z.number().min(1, "Personality must be at least 1").max(10, "Personality must be 10 or less"), z.null(), z.undefined()]).optional(),
  seeking_financial_min: z.union([z.number().min(1, "Financial status must be at least 1").max(5, "Financial status must be 5 or less"), z.null(), z.undefined()]).optional(),
  seeking_education: z.array(z.string()).optional(),
  seeking_description: z.string().trim().max(5000, "Description must be 5000 characters or less").optional(),
}).refine((data) => data.seeking_age_min <= data.seeking_age_max, {
  message: "Minimum age must be less than maximum age",
  path: ["seeking_age_max"],
});


// Religiosity values are stored lowercase (DB CHECK constraint), but we want
// to display Title-case in the UI. Map at render time only.
const RELIGIOSITY_LABELS: Record<string, string> = {
  practicing: "Practicing",
  moderate: "Moderate",
  learning: "Learning",
  "No preference": "No preference",
};

interface Props {
  data: ProfileFormData;
  onComplete: (data: Partial<ProfileFormData>) => void;
  onBack: () => void;
  saving: boolean;
  continueLabel?: string;
  loadingLabel?: string;
}

export function Step5Preferences({ data, onComplete, onBack, saving, continueLabel = "Save & Continue", loadingLabel = "Saving..." }: Props) {
  const [seekingDescLength, setSeekingDescLength] = useState(data.seeking_description?.length || 0);

  const isMale = data.gender === "male";

  // Gender-specific default weights
  const baseWeights: Record<string, ImportanceLevel> = {
    age: "important" as ImportanceLevel,
    sect: "important" as ImportanceLevel,
    height: "somewhat_important" as ImportanceLevel,
    location: "important" as ImportanceLevel,
    education: "somewhat_important" as ImportanceLevel,
    ethnicity: "flexible" as ImportanceLevel,
    financial: "somewhat_important" as ImportanceLevel,
    personality: "somewhat_important" as ImportanceLevel,
    religiosity: "very_important" as ImportanceLevel,
    marital_status: "somewhat_important" as ImportanceLevel,
    masjid_attendance: "important" as ImportanceLevel,
    citizenship: "somewhat_important" as ImportanceLevel
  };

  // Males get hijab weight, females get beard weight
  const genderSpecificWeights: Record<string, ImportanceLevel> = isMale
    ? { ...baseWeights, hijab: "very_important" as ImportanceLevel }
    : { ...baseWeights, beard: "very_important" as ImportanceLevel };

  const defaultWeights = (data.seeking_preferences_weights as Record<string, ImportanceLevel> | null) || genderSpecificWeights;

  const [importanceWeights, setImportanceWeights] = useState<Record<string, ImportanceLevel>>(defaultWeights);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(createSchema(isMale)),
    defaultValues: {
      seeking_age_min: data.seeking_age_min || 22,
      seeking_age_max: data.seeking_age_max || 35,
      seeking_height_min_feet: data.seeking_height_min_feet,
      seeking_height_min_inches: data.seeking_height_min_inches,
      seeking_height_max_feet: data.seeking_height_max_feet,
      seeking_height_max_inches: data.seeking_height_max_inches,
      seeking_ethnicity: (data.seeking_ethnicity && data.seeking_ethnicity.length > 0) ? data.seeking_ethnicity : ["No preference"],
      seeking_sect: (data.seeking_sect && data.seeking_sect.length > 0) ? data.seeking_sect : ["No preference"],
      seeking_religiosity: data.seeking_religiosity || [],
      seeking_masjid_attendance_min: data.seeking_masjid_attendance_min,
      seeking_location_preference: data.seeking_location_preference || "",
      seeking_hijab_preference: data.seeking_hijab_preference,
      seeking_beard_preference: data.seeking_beard_preference,
      seeking_citizenship: data.seeking_citizenship || [],
      seeking_marital_status: (data.seeking_marital_status && data.seeking_marital_status.length > 0) ? data.seeking_marital_status : ["No preference"],
      seeking_children_ok: data.seeking_children_ok !== undefined ? data.seeking_children_ok : true,
      seeking_convert_ok: data.seeking_convert_ok !== undefined ? data.seeking_convert_ok : true,
      seeking_divorced_ok: data.seeking_divorced_ok !== undefined ? data.seeking_divorced_ok : true,
      seeking_personality_min: data.seeking_personality_min || 1,
      seeking_personality_max: data.seeking_personality_max || 10,
      seeking_financial_min: data.seeking_financial_min,
      seeking_education: data.seeking_education || [],
      seeking_description: data.seeking_description || "",
    },
  });

  // Hydrate once on mount / when re-entering this step. Do NOT reset on every
  // parent re-render — that clobbered in-flight user input (bug B2).
  const hasHydrated = useRef(false);
  useEffect(() => {
    if (hasHydrated.current) return;
    hasHydrated.current = true;
    reset({
      seeking_age_min: data.seeking_age_min || 22,
      seeking_age_max: data.seeking_age_max || 35,
      seeking_height_min_feet: data.seeking_height_min_feet,
      seeking_height_min_inches: data.seeking_height_min_inches,
      seeking_height_max_feet: data.seeking_height_max_feet,
      seeking_height_max_inches: data.seeking_height_max_inches,
      seeking_ethnicity: (data.seeking_ethnicity && data.seeking_ethnicity.length > 0) ? data.seeking_ethnicity : ["No preference"],
      seeking_sect: (data.seeking_sect && data.seeking_sect.length > 0) ? data.seeking_sect : ["No preference"],
      seeking_religiosity: data.seeking_religiosity || [],
      seeking_masjid_attendance_min: data.seeking_masjid_attendance_min || undefined,
      seeking_location_preference: data.seeking_location_preference || undefined,
      seeking_hijab_preference: data.seeking_hijab_preference || undefined,
      seeking_beard_preference: data.seeking_beard_preference || undefined,
      seeking_citizenship: data.seeking_citizenship || [],
      seeking_marital_status: (data.seeking_marital_status && data.seeking_marital_status.length > 0) ? data.seeking_marital_status : ["No preference"],
      seeking_children_ok: data.seeking_children_ok !== undefined ? data.seeking_children_ok : true,
      seeking_convert_ok: data.seeking_convert_ok !== undefined ? data.seeking_convert_ok : true,
      seeking_divorced_ok: data.seeking_divorced_ok !== undefined ? data.seeking_divorced_ok : true,
      seeking_personality_min: data.seeking_personality_min || 1,
      seeking_personality_max: data.seeking_personality_max || 10,
      seeking_financial_min: data.seeking_financial_min,
      seeking_education: data.seeking_education || [],
      seeking_description: data.seeking_description || "",
    });
    setSeekingDescLength(data.seeking_description?.length || 0);
    const updatedWeights = (data.seeking_preferences_weights as Record<string, ImportanceLevel> | null) || genderSpecificWeights;
    setImportanceWeights(updatedWeights);
  }, [data, reset, genderSpecificWeights]);

  const seekingEthnicity = watch("seeking_ethnicity");
  const seekingSect = watch("seeking_sect");
  const seekingCitizenship = watch("seeking_citizenship");
  const seekingMaritalStatus = watch("seeking_marital_status");
  const seekingEducation = watch("seeking_education");
  const seekingReligiosity = watch("seeking_religiosity");
  const personalityMin = watch("seeking_personality_min");
  const personalityMax = watch("seeking_personality_max");
  const financialMin = watch("seeking_financial_min");

  const toggleArray = (field: any, value: string) => {
    const current: string[] = watch(field) || [];
    let next: string[];

    if (value === "No preference") {
      next = ["No preference"];
    } else {
      next = current.includes(value)
        ? current.filter((v: string) => v !== value)
        : [...current.filter((v: string) => v !== "No preference"), value];

      if (next.length === 0) next = ["No preference"];
    }

    setValue(field, next, { shouldDirty: true, shouldValidate: true, shouldTouch: true });
  };

  const onSubmit = (formData: any) => {
    // Clear gender-specific fields when not applicable
    const cleanedWeights = { ...importanceWeights };
    if (isMale) {
      // Males don't seek beard preference
      delete formData.seeking_beard_preference;
      delete cleanedWeights.beard;
    } else {
      // Females don't seek hijab preference
      delete formData.seeking_hijab_preference;
      delete cleanedWeights.hijab;
    }

    onComplete({
      ...formData,
      seeking_preferences_weights: cleanedWeights
    });
  };

  const onError = (_errors: any) => {
    // Form validation errors are surfaced to the user via the errors banner
    // and inline messages; no console output to avoid leaking form values.
  };

  return (
    <form onSubmit={handleSubmit(onSubmit, onError)} className="space-y-8">
      {Object.keys(errors).length > 0 && (
        <div className="rounded-lg bg-destructive/10 border-2 border-destructive p-4 text-sm">
          <h4 className="font-semibold text-destructive mb-2">⚠️ Please fix the following errors:</h4>
          <ul className="list-disc list-inside space-y-1 text-destructive">
            {errors.seeking_ethnicity && <li>{errors.seeking_ethnicity.message as string}</li>}
            {errors.seeking_sect && <li>{errors.seeking_sect.message as string}</li>}
            {errors.seeking_marital_status && <li>{errors.seeking_marital_status.message as string}</li>}
            {errors.seeking_description && <li>{errors.seeking_description.message as string}</li>}
            {errors.seeking_age_min && <li>{errors.seeking_age_min.message as string}</li>}
            {errors.seeking_age_max && <li>{errors.seeking_age_max.message as string}</li>}
          </ul>
        </div>
      )}

      <div className="rounded-lg bg-muted p-4 text-sm text-muted-foreground space-y-2">
        <p>These preferences help us match you better. Be realistic but don't be too restrictive!</p>
        <p className="font-medium">
          <strong>About Importance Levels:</strong> Use the importance selector below each preference to indicate how much it matters to you.
          Selecting "Don't care" makes that preference optional and won't affect your matches.
        </p>
      </div>

      <div className="space-y-6">
        <h3 className="text-lg font-semibold text-foreground">Basic Preferences</h3>
        
        <div className="space-y-4">
          <Label>Age range *</Label>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="seeking_age_min" className="text-sm text-muted-foreground">Minimum Age</Label>
              <Input
                id="seeking_age_min"
                type="number"
                {...register("seeking_age_min", { valueAsNumber: true })}
                min={18}
                max={60}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="seeking_age_max" className="text-sm text-muted-foreground">Maximum Age</Label>
              <Input
                id="seeking_age_max"
                type="number"
                {...register("seeking_age_max", { valueAsNumber: true })}
                min={18}
                max={60}
              />
            </div>
          </div>
          {errors.seeking_age_min && (
            <p className="text-sm text-destructive">{errors.seeking_age_min.message as string}</p>
          )}
          {errors.seeking_age_max && (
            <p className="text-sm text-destructive">{errors.seeking_age_max.message}</p>
          )}
          <CompactImportanceSelector
            label="age"
            value={importanceWeights.age as ImportanceLevel}
            onChange={(value) => setImportanceWeights({ ...importanceWeights, age: value })}
          />
        </div>

        <div className="space-y-2">
          <Label>Height preference</Label>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-sm text-muted-foreground">Minimum</Label>
              <div className="grid grid-cols-2 gap-2">
                <Select 
                  value={watch("seeking_height_min_feet")?.toString() || "any"} 
                  onValueChange={(value) => setValue("seeking_height_min_feet", value === "any" ? undefined : Number(value))}
                >
                  <SelectTrigger><SelectValue placeholder="Feet" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="any">Any</SelectItem>
                    {[4, 5, 6, 7].map((ft) => (
                      <SelectItem key={ft} value={String(ft)}>{ft} ft</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select 
                  value={watch("seeking_height_min_inches")?.toString() || "any"} 
                  onValueChange={(value) => setValue("seeking_height_min_inches", value === "any" ? undefined : Number(value))}
                >
                  <SelectTrigger><SelectValue placeholder="In" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="any">Any</SelectItem>
                    {Array.from({ length: 12 }, (_, i) => i).map((inch) => (
                      <SelectItem key={inch} value={String(inch)}>{inch} in</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-sm text-muted-foreground">Maximum</Label>
              <div className="grid grid-cols-2 gap-2">
                <Select 
                  value={watch("seeking_height_max_feet")?.toString() || "any"} 
                  onValueChange={(value) => setValue("seeking_height_max_feet", value === "any" ? undefined : Number(value))}
                >
                  <SelectTrigger><SelectValue placeholder="Feet" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="any">Any</SelectItem>
                    {[4, 5, 6, 7].map((ft) => (
                      <SelectItem key={ft} value={String(ft)}>{ft} ft</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select 
                  value={watch("seeking_height_max_inches")?.toString() || "any"} 
                  onValueChange={(value) => setValue("seeking_height_max_inches", value === "any" ? undefined : Number(value))}
                >
                  <SelectTrigger><SelectValue placeholder="In" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="any">Any</SelectItem>
                    {Array.from({ length: 12 }, (_, i) => i).map((inch) => (
                      <SelectItem key={inch} value={String(inch)}>{inch} in</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <CompactImportanceSelector
            label="height"
            value={importanceWeights.height as ImportanceLevel}
            onChange={(value) => setImportanceWeights({ ...importanceWeights, height: value })}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="seeking_location_preference">Location preference</Label>
          <Select
            value={watch("seeking_location_preference") || ""}
            onValueChange={(value) => setValue("seeking_location_preference", value)}
          >
            <SelectTrigger><SelectValue placeholder="Select location preference" /></SelectTrigger>
            <SelectContent>
              {SEEKING_LOCATION_PREFERENCE.map((option) => (
                <SelectItem key={option} value={option}>{option}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <CompactImportanceSelector
            label="location"
            value={importanceWeights.location as ImportanceLevel}
            onChange={(value) => setImportanceWeights({ ...importanceWeights, location: value })}
          />
        </div>

        <div className="space-y-3">
          <Label>Ethnicity preference *</Label>
          <div className="grid grid-cols-2 gap-2">
            {ETHNICITY_SEEKING.map((option) => (
              <div key={option} className="flex items-center space-x-2">
                <Checkbox
                  id={`seeking-ethnicity-${option}`}
                  checked={seekingEthnicity?.includes(option)}
                  onCheckedChange={() => toggleArray("seeking_ethnicity", option)}
                />
                <Label htmlFor={`seeking-ethnicity-${option}`} className="font-normal cursor-pointer text-sm">
                  {option}
                </Label>
              </div>
            ))}
          </div>
          {errors.seeking_ethnicity && (
            <p className="text-sm text-destructive">{errors.seeking_ethnicity.message}</p>
          )}
          <CompactImportanceSelector
            label="ethnicity"
            value={importanceWeights.ethnicity as ImportanceLevel}
            onChange={(value) => setImportanceWeights({ ...importanceWeights, ethnicity: value })}
          />
        </div>
      </div>

      <Separator />

      <div className="space-y-6">
        <h3 className="text-lg font-semibold text-foreground">Religious Preferences</h3>
        
        <div className="space-y-3">
          <Label>Preferred sect *</Label>
          <div className="flex flex-wrap gap-2">
            {SECT_SEEKING.map((option) => (
              <div key={option} className="flex items-center space-x-2">
                <Checkbox
                  id={`seeking-sect-${option}`}
                  checked={seekingSect?.includes(option)}
                  onCheckedChange={() => toggleArray("seeking_sect", option)}
                />
                <Label htmlFor={`seeking-sect-${option}`} className="font-normal cursor-pointer text-sm">
                  {option}
                </Label>
              </div>
            ))}
          </div>
          {errors.seeking_sect && (
            <p className="text-sm text-destructive">{errors.seeking_sect.message}</p>
          )}
          <CompactImportanceSelector
            label="sect"
            value={importanceWeights.sect as ImportanceLevel}
            onChange={(value) => setImportanceWeights({ ...importanceWeights, sect: value })}
          />
        </div>

        <div className="space-y-3">
          <Label>Religiosity level preference</Label>
          <div className="flex flex-wrap gap-2">
            {RELIGIOSITY_LEVEL_SEEKING.map((option) => (
              <div key={option} className="flex items-center space-x-2">
                <Checkbox
                  id={`seeking-religiosity-${option}`}
                  checked={seekingReligiosity?.includes(option)}
                  onCheckedChange={() => toggleArray("seeking_religiosity", option)}
                />
                <Label htmlFor={`seeking-religiosity-${option}`} className="font-normal cursor-pointer text-sm">
                  {RELIGIOSITY_LABELS[option] ?? option}
                </Label>
              </div>
            ))}
          </div>
          <CompactImportanceSelector
            label="religiosity"
            value={importanceWeights.religiosity as ImportanceLevel}
            onChange={(value) => setImportanceWeights({ ...importanceWeights, religiosity: value })}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="seeking_masjid_attendance_min">Minimum masjid attendance</Label>
          <Select
            value={watch("seeking_masjid_attendance_min") ?? undefined}
            onValueChange={(value) => setValue("seeking_masjid_attendance_min", value, { shouldDirty: true })}
          >
            <SelectTrigger><SelectValue placeholder="Select minimum attendance" /></SelectTrigger>
            <SelectContent>
              {MASJID_ATTENDANCE_SEEKING.map((option) => (
                <SelectItem key={option} value={option}>{option}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <CompactImportanceSelector
            label="masjid attendance"
            value={importanceWeights.masjid_attendance as ImportanceLevel}
            onChange={(value) => setImportanceWeights({ ...importanceWeights, masjid_attendance: value })}
          />
        </div>

        {isMale && (
          <div className="space-y-2">
            <Label htmlFor="seeking_hijab_preference">Hijab preference</Label>
            <Select
              value={watch("seeking_hijab_preference") ?? undefined}
              onValueChange={(value) => setValue("seeking_hijab_preference", value, { shouldDirty: true })}
            >
              <SelectTrigger><SelectValue placeholder="Select your preference" /></SelectTrigger>
              <SelectContent>
                {HIJAB_PREFERENCE.map((option) => (
                  <SelectItem key={option} value={option}>{option}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <CompactImportanceSelector
              label="hijab"
              value={importanceWeights.hijab as ImportanceLevel}
              onChange={(value) => setImportanceWeights({ ...importanceWeights, hijab: value })}
            />
          </div>
        )}

        {!isMale && (
          <div className="space-y-2">
            <Label htmlFor="seeking_beard_preference">Beard preference</Label>
            <Select
              value={watch("seeking_beard_preference") ?? undefined}
              onValueChange={(value) => setValue("seeking_beard_preference", value, { shouldDirty: true })}
            >
              <SelectTrigger><SelectValue placeholder="Select your preference" /></SelectTrigger>
              <SelectContent>
                {BEARD_PREFERENCE.map((option) => (
                  <SelectItem key={option} value={option}>{option}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <CompactImportanceSelector
              label="beard"
              value={importanceWeights.beard as ImportanceLevel}
              onChange={(value) => setImportanceWeights({ ...importanceWeights, beard: value })}
            />
          </div>
        )}
      </div>

      <Separator />

      <div className="space-y-6">
        <h3 className="text-lg font-semibold text-foreground">Background Preferences</h3>
        
        <div className="space-y-3">
          <Label>Citizenship preference</Label>
          <div className="flex flex-wrap gap-2">
            {CITIZENSHIP_STATUS_SEEKING.map((option) => (
              <div key={option} className="flex items-center space-x-2">
                <Checkbox
                  id={`seeking-citizenship-${option}`}
                  checked={seekingCitizenship?.includes(option)}
                  onCheckedChange={() => toggleArray("seeking_citizenship", option)}
                />
                <Label htmlFor={`seeking-citizenship-${option}`} className="font-normal cursor-pointer text-sm">
                  {option}
                </Label>
              </div>
            ))}
          </div>
          <CompactImportanceSelector
            label="citizenship"
            value={importanceWeights.citizenship as ImportanceLevel}
            onChange={(value) => setImportanceWeights({ ...importanceWeights, citizenship: value })}
          />
        </div>

        <div className="space-y-3">
          <Label>Marital status preference *</Label>
          <div className="flex flex-wrap gap-2">
            {MARITAL_STATUS_SEEKING.map((option) => (
              <div key={option} className="flex items-center space-x-2">
                <Checkbox
                  id={`seeking-marital-status-${option}`}
                  checked={seekingMaritalStatus?.includes(option)}
                  onCheckedChange={() => toggleArray("seeking_marital_status", option)}
                />
                <Label htmlFor={`seeking-marital-status-${option}`} className="font-normal cursor-pointer text-sm">
                  {option}
                </Label>
              </div>
            ))}
          </div>
          {errors.seeking_marital_status && (
            <p className="text-sm text-destructive">{errors.seeking_marital_status.message}</p>
          )}
          <CompactImportanceSelector
            label="marital status"
            value={importanceWeights.marital_status as ImportanceLevel}
            onChange={(value) => setImportanceWeights({ ...importanceWeights, marital_status: value })}
          />
        </div>

        <div className="space-y-4">
          <div className="flex items-center justify-between space-x-2 rounded-lg border p-4">
            <Label htmlFor="seeking_children_ok">Open to someone with children?</Label>
            <Switch
              id="seeking_children_ok"
              checked={watch("seeking_children_ok")}
              onCheckedChange={(checked) => setValue("seeking_children_ok", checked)}
            />
          </div>

          <div className="flex items-center justify-between space-x-2 rounded-lg border p-4">
            <Label htmlFor="seeking_convert_ok">Open to converts?</Label>
            <Switch
              id="seeking_convert_ok"
              checked={watch("seeking_convert_ok")}
              onCheckedChange={(checked) => setValue("seeking_convert_ok", checked)}
            />
          </div>

          <div className="flex items-center justify-between space-x-2 rounded-lg border p-4">
            <Label htmlFor="seeking_divorced_ok">Open to divorced?</Label>
            <Switch
              id="seeking_divorced_ok"
              checked={watch("seeking_divorced_ok")}
              onCheckedChange={(checked) => setValue("seeking_divorced_ok", checked)}
            />
          </div>
        </div>
      </div>

      <Separator />

      <div className="space-y-6">
        <h3 className="text-lg font-semibold text-foreground">Personality & Lifestyle</h3>
        
        <div className="space-y-4">
          <Label>Personality preference range</Label>
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span>Min: {personalityMin}</span>
              <span>Max: {personalityMax}</span>
            </div>
            <Slider
              value={[personalityMin || 1, personalityMax || 10]}
              onValueChange={(value) => {
                setValue("seeking_personality_min", value[0]);
                setValue("seeking_personality_max", value[1]);
              }}
              min={1}
              max={10}
              step={1}
              className="w-full"
              minStepsBetweenThumbs={1}
            />
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>Introverted</span>
              <span>Extroverted</span>
            </div>
          </div>
          <CompactImportanceSelector
            label="personality"
            value={importanceWeights.personality as ImportanceLevel}
            onChange={(value) => setImportanceWeights({ ...importanceWeights, personality: value })}
          />
        </div>

        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <Label>Minimum financial independence</Label>
            {financialMin && <span className="text-sm font-medium text-primary">{financialMin}/5</span>}
          </div>
          <Slider
            value={[financialMin || 1]}
            onValueChange={(value) => setValue("seeking_financial_min", value[0])}
            min={1}
            max={5}
            step={1}
            className="w-full"
          />
          <CompactImportanceSelector
            label="financial"
            value={importanceWeights.financial as ImportanceLevel}
            onChange={(value) => setImportanceWeights({ ...importanceWeights, financial: value })}
          />
        </div>

        <div className="space-y-3">
          <Label>Education preference</Label>
          <div className="grid grid-cols-2 gap-2">
            {EDUCATION_LEVEL_SEEKING.map((option) => (
              <div key={option} className="flex items-center space-x-2">
                <Checkbox
                  id={`seeking-education-${option}`}
                  checked={seekingEducation?.includes(option)}
                  onCheckedChange={() => toggleArray("seeking_education", option)}
                />
                <Label htmlFor={`seeking-education-${option}`} className="font-normal cursor-pointer text-sm">
                  {option}
                </Label>
              </div>
            ))}
          </div>
          <CompactImportanceSelector
            label="education"
            value={importanceWeights.education as ImportanceLevel}
            onChange={(value) => setImportanceWeights({ ...importanceWeights, education: value })}
          />
        </div>
      </div>

      <Separator />

      <div className="space-y-6">
        <h3 className="text-lg font-semibold text-foreground">In Your Own Words</h3>
        
        <div className="space-y-2">
          <div className="flex justify-between items-center">
            <Label htmlFor="seeking_description">What are you looking for in a spouse? *</Label>
            <span className="text-sm text-muted-foreground">{seekingDescLength}/5000</span>
          </div>
          <Textarea
            id="seeking_description"
            {...register("seeking_description")}
            placeholder="Describe your ideal spouse - personality, values, goals, lifestyle..."
            rows={6}
            maxLength={5000}
            onChange={(e) => {
              register("seeking_description").onChange(e);
              setSeekingDescLength(e.target.value.length);
            }}
          />
          <p className="text-sm text-muted-foreground">
            Be specific about what matters most to you in a life partner
          </p>
          {errors.seeking_description && (
            <p className="text-sm text-destructive">{errors.seeking_description.message}</p>
          )}
        </div>
      </div>

      <div className="flex justify-between gap-4">
        <Button type="button" variant="outline" onClick={onBack}>
          Back
        </Button>
        <Button type="submit" disabled={saving} className="min-w-32">
          {saving ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              {loadingLabel}
            </>
          ) : (
            continueLabel
          )}
        </Button>
      </div>
    </form>
  );
}
